// architecture.ts

import type {
  ImmutableResult,
  InitialProperties,
  Cmd,
  ImmutableProgram,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type FlySidenav from './index.js';
import {isJourneyRoute} from '#lit/utils/journeys.js';
import {NavEvents} from '#lit/types/events.js';
import {teamsService} from '#lit/services/teams.js';

export type SelectedTeam = {
  id: number;
  name: string;
  organization: {
    name: string;
  };
};

export type Model = {
  collapsed: boolean;
  isOpen: boolean;
  selectedOption: SelectedTeam | null;
  teams: SelectedTeam[];
  filteredTeams: SelectedTeam[];
  queryTerm: string;
  error: unknown | null;
};

export const Msg = makeVariants({
  toggleMenu: Variant<void>()('toggle-menu'),
  toggleCollapse: Variant<void>()('toggle-collapse'),
  openNav: Variant<void>()('open-nav'),
  closeNav: Variant<void>()('close-nav'),
  teamUpdateRequest: Variant<SelectedTeam>()('team-update-request'),
  isOpen: Variant<boolean>()('is-open'),
  teamUpdateSuccess: Variant<SelectedTeam>()('team-update-success'),
  teamUpdateFailure: Variant<unknown>()('team-update-failure'),
  filterTeams: Variant<string>()('filter-teams'),
});

/**
 * If the user switches teams in these pages, page is reloaded
 * If he switches teams in other pages, he will be redirected to audiences
 */
const ReloadLocations = [
  'audiences',
  'source_connections',
  'signal_routes',
  'destination_platforms',
  'journeys',
  'review_audiences',
  'models',
  'import_sources',
  'organization',
];

export type Msg = VariantsFor<typeof Msg>;

function dispatchCollapse(host: FlySidenav) {
  const appWrapper = document.querySelector('#app');
  if (appWrapper) {
    if (host.model.collapsed) {
      appWrapper.classList.add('collapsed');
    } else {
      appWrapper.classList.remove('collapsed');
    }
  }

  saveNavCollapseToLocalStorage(host.model.collapsed);

  const event = new CustomEvent(NavEvents.TOGGLE_COLLAPSE, {
    detail: {
      collapsed: host.model.collapsed,
    },
  });
  window.dispatchEvent(event);
}

function saveNavCollapseToLocalStorage(collapsed: boolean) {
  return localStorage.setItem(
    'settings',
    JSON.stringify({
      navCollapse: collapsed,
    })
  );
}

function getNavCollapseFromSettings() {
  if (isJourneyRoute()) {
    saveNavCollapseToLocalStorage(true);
    return true;
  } else {
    return (
      JSON.parse(localStorage.getItem('settings') || '{}')['navCollapse'] ??
      false
    );
  }
}

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const teams = <SelectedTeam[] | undefined>props.get('teams') ?? [];
  return [
    {
      collapsed: getNavCollapseFromSettings(),
      isOpen: <boolean>props.get('open') ?? false,
      selectedOption: <SelectedTeam | null>props.get('selectedOption') ?? null,
      teams,
      filteredTeams: teams,
      queryTerm: '',
      error: null,
    } as const,
    cmd((host: FlySidenav) => {
      dispatchCollapse(host);
    }),
  ];
}

async function updateSelectedTeam(
  host: FlySidenav,
  selectedTeam: SelectedTeam
) {
  try {
    await teamsService.update(selectedTeam);
    host.handleUpdate(Msg.teamUpdateSuccess(selectedTeam));
  } catch (err: unknown) {
    host.handleUpdate(Msg.teamUpdateFailure(err));
  }
}

function pageReload() {
  const urlEnding = window.location.pathname.split('/').pop() || '';
  if (ReloadLocations.includes(urlEnding)) {
    location.reload();
    return;
  }

  location.href = new URL('audiences', window.location.origin).href;
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'toggle-menu':
      return {...model, isOpen: !model.isOpen} as const;
    case 'toggle-collapse':
      return [{...model, collapsed: !model.collapsed}, cmd(dispatchCollapse)];
    case 'open-nav':
      return [{...model, collapsed: false}, cmd(dispatchCollapse)];
    case 'close-nav':
      return [{...model, collapsed: true}, cmd(dispatchCollapse)];
    case 'team-update-request':
      return [
        {...model},
        cmd(async (host: FlySidenav) => {
          const selectedTeam = msg.value;
          await updateSelectedTeam(host, selectedTeam);
        }),
      ];
    case 'team-update-success':
      return [{...model}, cmd(pageReload)] as const;
    case 'team-update-failure':
      return {
        ...model,
        error: msg.value,
        isOpen: false,
      } as const;
    case 'is-open':
      return {...model, isOpen: msg.value} as const;
    case 'filter-teams': {
      const queryTerm = msg.value;
      const filteredTeams = queryTerm
        ? model.teams.filter(team =>
            team.name.toLowerCase().includes(queryTerm.toLowerCase())
          )
        : model.teams;
      return {...model, queryTerm, filteredTeams} as const;
    }
  }
}

export const main: ImmutableProgram<Model, Msg> = {init, update};
